import { useState } from 'react';
import { ChevronRight, ChevronDown, File, Folder } from 'lucide-react';
import { clsx } from 'clsx';

interface FileNode {
  name: string;
  type: 'file' | 'directory';
  path: string;
  children?: FileNode[];
}

interface FileTreeProps {
  files: FileNode[];
  onFileSelect?: (path: string) => void;
  selectedFile?: string;
}

export function FileTree({ files, onFileSelect, selectedFile }: FileTreeProps) {
  return (
    <div className="rounded-lg border border-dark-200 bg-dark-50 p-3">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-primary-400">
        <Folder size={16} />
        <span>Files</span>
      </div>
      <div className="space-y-1">
        {files.map((node) => (
          <FileTreeNode
            key={node.path}
            node={node}
            onFileSelect={onFileSelect}
            selectedFile={selectedFile}
            level={0}
          />
        ))}
      </div>
    </div>
  );
}

interface FileTreeNodeProps {
  node: FileNode;
  onFileSelect?: (path: string) => void;
  selectedFile?: string;
  level: number;
}

function FileTreeNode({
  node,
  onFileSelect,
  selectedFile,
  level,
}: FileTreeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleClick = () => {
    if (node.type === 'directory') {
      setIsExpanded(!isExpanded);
    } else {
      onFileSelect?.(node.path);
    }
  };

  const isSelected = selectedFile === node.path;
  const paddingLeft = `${level * 16 + 8}px`;

  return (
    <div>
      <div
        className={clsx(
          'flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer transition-colors',
          isSelected
            ? 'bg-primary-950 text-primary-300 border border-primary-600/50'
            : 'text-primary-200 hover:bg-dark-100 hover:text-primary-300'
        )}
        style={{ paddingLeft }}
        onClick={handleClick}
      >
        {node.type === 'directory' ? (
          <>
            {isExpanded ? (
              <ChevronDown size={14} className="flex-shrink-0" />
            ) : (
              <ChevronRight size={14} className="flex-shrink-0" />
            )}
            <Folder size={14} className="flex-shrink-0 text-primary-500" />
          </>
        ) : (
          <>
            <div className="w-[14px]" />
            <File size={14} className="flex-shrink-0 text-primary-400" />
          </>
        )}
        <span className="text-sm truncate">{node.name}</span>
      </div>

      {node.type === 'directory' && isExpanded && node.children && (
        <div>
          {node.children.map((child) => (
            <FileTreeNode
              key={child.path}
              node={child}
              onFileSelect={onFileSelect}
              selectedFile={selectedFile}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}
